// Флажок, радио и переключатель (components/Choice/README.md).
// Системные поля ввода с классами t-check, t-radio, t-toggle: оформление bundle.css построено
// на :checked, а доступность даёт сам браузер. Подпись справа, клик по подписи переключает.
import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '../lib/cx.ts';

interface ChoiceBaseProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export interface CheckboxProps extends ChoiceBaseProps {
  /** Круглый флажок «готово» в личном списке. */
  round?: boolean;
}

export function Checkbox({ label, round, className, ...input }: CheckboxProps) {
  return (
    <label className={cx('t-choice', className)}>
      <input {...input} type="checkbox" className={cx('t-check', round && 'round')} />
      {label}
    </label>
  );
}

/** Переключатель: настройка, которая применяется сразу. Не для формы с «Сохранить». */
export function Switch({ label, className, ...input }: ChoiceBaseProps) {
  return (
    <label className={cx('t-choice', className)}>
      <input {...input} type="checkbox" role="switch" className="t-toggle" />
      {label}
    </label>
  );
}

export interface RadioOption<V extends string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string> {
  /** Подпись группы для программ чтения экрана. */
  label: string;
  value: V | null;
  onChange: (value: V) => void;
  options: readonly RadioOption<V>[];
  name?: string;
  /** Варианты в строку (по умолчанию) или столбцом. */
  direction?: 'row' | 'column';
  className?: string;
}

/** Один вариант из 2–4 в форме. */
export function RadioGroup<V extends string>({
  label,
  value,
  onChange,
  options,
  name,
  direction = 'row',
  className,
}: RadioGroupProps<V>) {
  const autoName = useId();
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx(direction === 'row' ? 't-row' : 't-col', className)}
    >
      {options.map((option) => (
        <label key={option.value} className="t-choice">
          <input
            type="radio"
            className="t-radio"
            name={name ?? autoName}
            value={option.value}
            checked={value === option.value}
            disabled={option.disabled}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}
